/**
 * Analytics utility functions for consistent event tracking
 * 
 * Provides standardized functions for tracking user interactions,
 * ensuring consistent event naming and property structure across the app.
 */

/**
 * Universal click tracking utility
 * Use this for all important buttons and links
 * 
 * @param {Object} posthog - PostHog instance from usePostHog()
 * @param {string} eventName - Name of the event (snake_case)
 * @param {Object} properties - Additional event properties
 * 
 * @example
 * ```javascript
 * trackClick(posthog, 'button_clicked', {
 *   button_type: 'cta',
 *   location: 'homepage',
 * });
 * ```
 */
export function trackClick(posthog, eventName, properties = {}) {
  if (!posthog || typeof posthog.capture !== 'function') {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Analytics] PostHog not available, event not tracked:', eventName);
    }
    return;
  }

  const defaultProperties = {
    timestamp: new Date().toISOString(),
    url: typeof window !== 'undefined' ? window.location.href : null,
    pathname: typeof window !== 'undefined' ? window.location.pathname : null,
  };

  posthog.capture(eventName, {
    ...defaultProperties,
    ...properties,
  });
}

/**
 * Track CTA clicks with standardized context
 * 
 * @param {Object} posthog - PostHog instance
 * @param {string} ctaType - Type of CTA ('upgrade', 'sign_in', 'get_started', 'learn_more')
 * @param {Object} properties - Additional context properties
 * 
 * @example
 * ```javascript
 * trackCTAClick(posthog, 'upgrade', {
 *   source: 'pricing_card',
 *   location: 'homepage',
 *   price_id: 'price_xxx',
 * });
 * ```
 */
export function trackCTAClick(posthog, ctaType, properties = {}) {
  trackClick(posthog, 'cta_clicked', {
    cta_type: ctaType,
    ...properties,
  });
}

/**
 * Track navigation events
 * 
 * @param {Object} posthog - PostHog instance
 * @param {string} destination - Destination path/URL
 * @param {Object} properties - Additional navigation context
 * 
 * @example
 * ```javascript
 * trackNavigation(posthog, '/pricing', {
 *   source: 'header',
 *   link_text: 'Pricing',
 * });
 * ```
 */
export function trackNavigation(posthog, destination, properties = {}) {
  trackClick(posthog, 'navigation_clicked', {
    destination,
    ...properties,
  });
}

/**
 * Track page engagement metrics
 * 
 * @param {Object} posthog - PostHog instance
 * @param {string} metricType - Type of engagement ('scroll', 'time', 'interaction')
 * @param {Object} properties - Engagement metrics
 * 
 * @example
 * ```javascript
 * trackEngagement(posthog, 'scroll', {
 *   scroll_depth: 75,
 *   page: '/pricing',
 * });
 * ```
 */
export function trackEngagement(posthog, metricType, properties = {}) {
  trackClick(posthog, `engagement_${metricType}`, properties);
}

/**
 * Track conversion funnel events
 * 
 * @param {Object} posthog - PostHog instance
 * @param {string} funnelStage - Stage in conversion funnel
 * @param {Object} properties - Stage-specific properties
 * 
 * @example
 * ```javascript
 * trackFunnelStage(posthog, 'pricing_viewed', {
 *   source: 'homepage_cta',
 *   tier: 'free',
 * });
 * ```
 */
export function trackFunnelStage(posthog, funnelStage, properties = {}) {
  trackClick(posthog, funnelStage, {
    funnel_name: 'free_to_premium',
    ...properties,
  });
}
